import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PartSecAttribute, PartSecAttributeSearchParams } from '../../services/partSecAttributesApi';

export interface PartSecAttributesState {
  items: PartSecAttribute[];
  selectedItem: PartSecAttribute | null;
  loading: boolean;
  error: string | null;
  formData: { PartAttributeId: number | null; Name: string; Description: string; Active: boolean };
  validationErrors: string[];
  searchParams: PartSecAttributeSearchParams;
  pagination: { page: number; pageSize: number; total: number };
  statistics: { total: number; active: number; inactive: number };
  selectedIds: number[];
  bulkOperationLoading: boolean;
  successMessage: string;
}

const initialState: PartSecAttributesState = {
  items: [], selectedItem: null, loading: false, error: null,
  formData: { PartAttributeId: null, Name: '', Description: '', Active: true },
  validationErrors: [],
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'Name', sortOrder: 'asc' },
  pagination: { page: 1, pageSize: 10, total: 0 },
  statistics: { total: 0, active: 0, inactive: 0 },
  selectedIds: [], bulkOperationLoading: false, successMessage: '',
};

const partSecAttributesSlice = createSlice({
  name: 'partSecAttributes', initialState,
  reducers: {
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; },
    setItems: (s, a: PayloadAction<PartSecAttribute[]>) => { s.items = a.payload; },
    setSelectedItem: (s, a: PayloadAction<PartSecAttribute | null>) => { s.selectedItem = a.payload; },
    addItem: (s, a: PayloadAction<PartSecAttribute>) => { s.items.unshift(a.payload); },
    updateItem: (s, a: PayloadAction<PartSecAttribute>) => {
      const i = s.items.findIndex(x => x.Id === a.payload.Id);
      if (i !== -1) s.items[i] = a.payload;
      if (s.selectedItem?.Id === a.payload.Id) s.selectedItem = a.payload;
    },
    removeItem: (s, a: PayloadAction<number>) => {
      s.items = s.items.filter(x => x.Id !== a.payload);
      if (s.selectedItem?.Id === a.payload) s.selectedItem = null;
    },
    setFormData: (s, a: PayloadAction<Partial<PartSecAttributesState['formData']>>) => { s.formData = { ...s.formData, ...a.payload }; },
    resetFormData: (s) => { s.formData = initialState.formData; },
    setValidationErrors: (s, a: PayloadAction<string[]>) => { s.validationErrors = a.payload; },
    clearValidationErrors: (s) => { s.validationErrors = []; },
    setSearchParams: (s, a: PayloadAction<Partial<PartSecAttributeSearchParams>>) => { s.searchParams = { ...s.searchParams, ...a.payload } as any; },
    resetSearchParams: (s) => { s.searchParams = initialState.searchParams; },
    setPagination: (s, a: PayloadAction<Partial<PartSecAttributesState['pagination']>>) => { s.pagination = { ...s.pagination, ...a.payload } as any; },
    setStatistics: (s, a: PayloadAction<PartSecAttributesState['statistics']>) => { s.statistics = a.payload; },
    setSelectedIds: (s, a: PayloadAction<number[]>) => { s.selectedIds = a.payload; },
    addSelectedId: (s, a: PayloadAction<number>) => { if (!s.selectedIds.includes(a.payload)) s.selectedIds.push(a.payload); },
    removeSelectedId: (s, a: PayloadAction<number>) => { s.selectedIds = s.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (s) => { s.selectedIds = []; },
    setBulkOperationLoading: (s, a: PayloadAction<boolean>) => { s.bulkOperationLoading = a.payload; },
    setSuccessMessage: (s, a: PayloadAction<string>) => { s.successMessage = a.payload; },
    clearSuccessMessage: (s) => { s.successMessage = ''; },
    resetPartSecAttributesState: () => initialState,
  }
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setFormData, resetFormData, setValidationErrors, clearValidationErrors,
  setSearchParams, resetSearchParams, setPagination, setStatistics,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds, setBulkOperationLoading,
  setSuccessMessage, clearSuccessMessage, resetPartSecAttributesState,
} = partSecAttributesSlice.actions;

export const FETCH_PART_SEC_ATTRIBUTES = 'partSecAttributes/fetch';
export const FETCH_PART_SEC_ATTRIBUTE_BY_ID = 'partSecAttributes/fetchById';
export const CREATE_PART_SEC_ATTRIBUTE = 'partSecAttributes/create';
export const UPDATE_PART_SEC_ATTRIBUTE = 'partSecAttributes/update';
export const DELETE_PART_SEC_ATTRIBUTE = 'partSecAttributes/delete';
export const SEARCH_PART_SEC_ATTRIBUTES = 'partSecAttributes/search';
export const FETCH_PART_SEC_ATTRIBUTES_STATS = 'partSecAttributes/fetchStats';
export const BULK_PART_SEC_ATTRIBUTES_ACTIVATE = 'partSecAttributes/bulkActivate';
export const BULK_PART_SEC_ATTRIBUTES_DEACTIVATE = 'partSecAttributes/bulkDeactivate';
export const BULK_PART_SEC_ATTRIBUTES_DELETE = 'partSecAttributes/bulkDelete';
export const EXPORT_PART_SEC_ATTRIBUTES_CSV = 'partSecAttributes/exportCsv';

export const fetchPartSecAttributes = (p?: PartSecAttributeSearchParams) => ({ type: FETCH_PART_SEC_ATTRIBUTES, payload: p });
export const fetchPartSecAttributeById = (id: number) => ({ type: FETCH_PART_SEC_ATTRIBUTE_BY_ID, payload: id });
export const createPartSecAttribute = (item: Omit<PartSecAttribute, 'Id'>) => ({ type: CREATE_PART_SEC_ATTRIBUTE, payload: item });
export const updatePartSecAttribute = (id: number, item: Partial<PartSecAttribute>) => ({ type: UPDATE_PART_SEC_ATTRIBUTE, payload: { id, item } });
export const deletePartSecAttribute = (id: number) => ({ type: DELETE_PART_SEC_ATTRIBUTE, payload: id });
export const searchPartSecAttributes = (p: PartSecAttributeSearchParams) => ({ type: SEARCH_PART_SEC_ATTRIBUTES, payload: p });
export const fetchPartSecAttributesStats = () => ({ type: FETCH_PART_SEC_ATTRIBUTES_STATS });
export const bulkPartSecAttributesActivate = (ids: number[]) => ({ type: BULK_PART_SEC_ATTRIBUTES_ACTIVATE, payload: ids });
export const bulkPartSecAttributesDeactivate = (ids: number[]) => ({ type: BULK_PART_SEC_ATTRIBUTES_DEACTIVATE, payload: ids });
export const bulkPartSecAttributesDelete = (ids: number[]) => ({ type: BULK_PART_SEC_ATTRIBUTES_DELETE, payload: ids });
export const exportPartSecAttributesCsv = (p?: PartSecAttributeSearchParams) => ({ type: EXPORT_PART_SEC_ATTRIBUTES_CSV, payload: p });

export default partSecAttributesSlice.reducer; 